import moment from 'moment';

/**
 * THIN-8151 / THIN-8287: the calendar window a 5-Day (PPS) assessment reference date may fall in inside one
 * Medicare A / Managed Care A payer period -- MDS days 1 to 8 of that period, as the server resolves it
 * (`GET /provider/mds-assessment/{patientId}/five-day-ard-windows`, one per Part A payer row).
 * `lastAllowedDate` is null when an open-ended payer never reaches day 8.
 */
export type FiveDayArdWindow = {
  payerStartDate: string;
  payerEndDate: string | null;
  lastAllowedDate: string | null;
};

export type ArdBounds = {
  /** The picker's navigable range; `shouldDisableDate` greys the dates inside it that the rule rejects. */
  minDate: string | null;
  maxDate: string | null;
  /** The ordinary lower edge, for dates no Part A payer covers. */
  firstEOCStartDate: string | null;
  /** Set only when the 5-Day rule boxes the picker: every Part A payer period of the patient. */
  fiveDayWindows: FiveDayArdWindow[] | null;
};

type DateLike = Date | string | null | undefined;

// Strict ISO for strings: a loose parse of garbage falls back to "now" with a console warning.
const toMoment = (date: DateLike): moment.Moment | null => {
  if (!date) return null;
  const parsed = typeof date === 'string' ? moment(date, moment.ISO_8601, true) : moment(date);
  return parsed.isValid() ? parsed : null;
};

/** The Part A payer period containing the date, if any. */
const windowCovering = (date: moment.Moment, bounds: ArdBounds): FiveDayArdWindow | undefined =>
  bounds.fiveDayWindows?.find(
    (window) =>
      !date.isBefore(window.payerStartDate, 'day') &&
      (!window.payerEndDate || !date.isAfter(window.payerEndDate, 'day'))
  );

/**
 * The picker edges for an assessment reference date. A 5-Day (PPS) with a Part A payer opens at the earlier
 * of the first payer start and the EOC start -- the days before certification stay reachable -- and, once an
 * open-ended Part A payer has passed its day 8, closes there, since every later date sits inside that payer.
 * Every other case keeps today's EOC-anchored lower edge and no upper edge.
 */
export const getArdBounds = (
  isFiveDay: boolean,
  windows: FiveDayArdWindow[] | null | undefined,
  firstEOCStartDate: string | null | undefined
): ArdBounds => {
  const eocStart = firstEOCStartDate ?? null;
  if (isFiveDay && Array.isArray(windows) && windows.length > 0) {
    const starts = windows.map((window) => window.payerStartDate);
    if (eocStart) starts.push(eocStart);
    const minDate = starts.reduce((earliest, start) =>
      moment(start).isBefore(earliest, 'day') ? start : earliest
    );
    const open = windows.find((window) => !window.payerEndDate);
    return {
      minDate,
      maxDate: open?.lastAllowedDate ?? null,
      firstEOCStartDate: eocStart,
      fiveDayWindows: windows,
    };
  }
  return { minDate: eocStart, maxDate: null, firstEOCStartDate: eocStart, fiveDayWindows: null };
};

const MESSAGE_PREFIX = 'A 5-Day (PPS) assessment must be dated';

const fmt = (date: string) => moment(date).format('MMM D, YYYY');

/** Same copy the server returns, so the inline message reads the same whichever side caught it. */
export const fiveDayArdWindowMessage = (payerStartDate: string, lastAllowedDate: string): string =>
  `${MESSAGE_PREFIX} within MDS days 1 to 8 (${fmt(payerStartDate)} to ${fmt(
    lastAllowedDate
  )}). Choose a date on or before ${fmt(lastAllowedDate)}.`;

const errorFor = (picked: moment.Moment, bounds: ArdBounds): string | null => {
  const window = windowCovering(picked, bounds);
  if (!window?.lastAllowedDate || !picked.isAfter(window.lastAllowedDate, 'day')) return null;
  return fiveDayArdWindowMessage(window.payerStartDate, window.lastAllowedDate);
};

/**
 * The inline message when the date sits inside a Part A payer period but after its MDS day 8, else null.
 * A date no Part A payer covers is not this rule's concern -- the EOC lower edge still applies to it.
 */
export const getFiveDayArdError = (date: DateLike, bounds: ArdBounds): string | null => {
  const picked = toMoment(date);
  return picked ? errorFor(picked, bounds) : null;
};

/** True when a Part A payer covers the date, so the 5-Day window -- not the EOC start -- is its lower edge. */
export const isInsideFiveDayWindow = (date: DateLike, bounds: ArdBounds): boolean => {
  const picked = toMoment(date);
  return !!picked && !!windowCovering(picked, bounds);
};

/** The picker's `shouldDisableDate`: the 5-Day rule inside a Part A payer, the EOC lower edge outside one. */
export const isArdDateDisabled = (date: DateLike, bounds: ArdBounds): boolean => {
  const picked = toMoment(date);
  if (!picked) return false;
  if (windowCovering(picked, bounds)) return !!errorFor(picked, bounds);
  return !!bounds.firstEOCStartDate && picked.isBefore(bounds.firstEOCStartDate, 'day');
};

/** True for this rule's message, whether the form or the server produced it. */
export const isFiveDayArdWindowError = (message: unknown): message is string =>
  typeof message === 'string' && message.startsWith(MESSAGE_PREFIX);
